import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useCan } from '../../lib/api/hooks';

/**
 * Offering a spare licence seat to the scanner in front of the operator.
 *
 * On a per-device unlimited plan a scanner cannot capture until a seat is allocated to it, and allocation
 * only ever happened on the Devices page. So the answer to "why can't I capture?" was a link to another
 * screen, a button to find, and a trip back — while the seat they had already paid for sat idle. When we
 * can see the scanner, see the free seat, and know who is asking, the useful thing to do is ask.
 *
 * Keyed on the connected serial rather than a precheck response so any screen that knows which scanner is
 * attached can use it.
 */

interface DeviceRow {
  deviceId: string;
  serialNumber: string;
  name?: string;
  restricted: boolean;
}
interface LicenseRow {
  licenseId: string;
  status: string;
  deviceId: string | null;
  cooldownUntil: string | null;
}

export type SeatState =
  /** Not a per-device plan (or no scanner known): seats are irrelevant, say nothing. */
  | { kind: 'not-applicable' }
  /** This scanner already holds a seat. */
  | { kind: 'licensed' }
  /** A seat is free and the caller may allocate it. */
  | { kind: 'offer'; deviceId: string; licenseId: string; reattach: boolean; spare: number }
  /** A seat is needed, but there is none free, or this scanner may not hold one. */
  | { kind: 'blocked'; reason: 'no-seat' | 'restricted' }
  /** This caller cannot see or allocate seats, so nothing can be determined from here. */
  | { kind: 'not-permitted' };

/**
 * Resolve what to do about the connected scanner's licence.
 *
 * Reads are gated on capability: an Operator holds neither ManageDevices nor AllocateLicenses, so asking
 * for /devices or /licenses would only produce 403s. They resolve to `not-permitted` — which is "cannot
 * tell", NOT "a licence is missing". Announcing a licence problem to someone who cannot check would be
 * wrong for the many operators on pay-per-transaction plans, where no seat is needed at all; the prompt
 * only voices it where the caller has independently established that this scanner needs one.
 */
export function useScannerSeat(serial: string | undefined): SeatState {
  const can = useCan();
  const mayAllocate = can('AllocateLicenses');
  const mayRead = mayAllocate || can('ManageDevices');
  const enabled = Boolean(serial) && mayRead;

  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.get<DeviceRow[]>('/devices'),
    enabled,
  });
  const licenses = useQuery({
    queryKey: ['licenses'],
    queryFn: () => api.get<LicenseRow[]>('/licenses'),
    enabled: enabled && mayAllocate,
  });

  return useMemo<SeatState>(() => {
    if (!serial) return { kind: 'not-applicable' };
    if (!mayAllocate) return { kind: 'not-permitted' };
    if (!devices.data || !licenses.data) return { kind: 'not-applicable' };
    /*
     * Licence rows ARE the per-device plan: they exist only because one was bought. Nothing to say when
     * there are none — a pay-per-transaction customer captures on any scanner and has no seats by design.
     *
     * Deliberately NOT read from /subscriptions, which reconciles against Stripe on every call. That
     * belongs on the billing page, not on the path an operator walks before every capture.
     */
    if (licenses.data.length === 0) return { kind: 'not-applicable' };

    // The server stores serials trimmed + uppercased; the Device Service reports them as the device does.
    const want = serial.trim().toUpperCase();
    const device = devices.data.find((d) => d.serialNumber.trim().toUpperCase() === want);
    // Not registered yet — nothing to allocate a seat to. The capture precheck registers it, so this
    // resolves itself the moment a capture is attempted.
    if (!device) return { kind: 'not-applicable' };
    if (licenses.data.some((l) => l.deviceId === device.deviceId && l.status === 'ALLOCATED')) {
      return { kind: 'licensed' };
    }
    if (device.restricted) return { kind: 'blocked', reason: 'restricted' };

    // A seat freed from THIS scanner can go back on it immediately — no cooldown wait — so prefer it over
    // spending a spare seat that a different scanner may need.
    const reattach = licenses.data.find((l) => l.deviceId === device.deviceId && l.status === 'DEALLOCATING');
    const available = licenses.data.filter((l) => l.status === 'AVAILABLE');
    const seat = reattach ?? available[0];
    if (!seat) return { kind: 'blocked', reason: 'no-seat' };
    return {
      kind: 'offer',
      deviceId: device.deviceId,
      licenseId: seat.licenseId,
      reattach: Boolean(reattach),
      spare: available.length,
    };
  }, [serial, devices.data, licenses.data, mayAllocate]);
}

/**
 * The question itself. Renders nothing unless there is something to ask or explain, so it is safe to drop
 * into any screen that has a connected scanner.
 *
 * `onAllocated` lets the host re-run whatever check it was blocked on.
 */
export function ScannerSeatPrompt({
  serial,
  onAllocated,
  required = false,
  children,
}: {
  serial: string | undefined;
  onAllocated?: () => void;
  /**
   * The host already knows this scanner is being refused for want of a licence (e.g. the capture precheck
   * returned NO_DEVICE_LICENSE). Guarantees something is rendered — guidance for a caller who cannot
   * allocate, and `children` as the fallback while the seat data loads — so the operator is never left
   * with a bare error and no next step.
   */
  required?: boolean;
  children?: ReactNode;
}) {
  const seat = useScannerSeat(serial);
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const allocate = useMutation({
    mutationFn: (v: { licenseId: string; deviceId: string }) => api.post('/licenses/allocate', v),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['licenses'] }),
        qc.invalidateQueries({ queryKey: ['devices'] }),
      ]);
      onAllocated?.();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not allocate the license'),
  });

  if (seat.kind === 'licensed') return null;
  if (seat.kind === 'not-applicable' || seat.kind === 'not-permitted') {
    if (!required) return null;
    return (
      <div className="seat-offer">
        {seat.kind === 'not-permitted' ? (
          <span>This scanner needs a device license. Ask an account administrator to allocate one to it.</span>
        ) : (
          children
        )}
      </div>
    );
  }

  if (seat.kind === 'blocked') {
    return (
      <div className="seat-offer">
        {seat.reason === 'restricted' ? (
          <span>
            This scanner is restricted pending review and can't hold a license. Contact{' '}
            <Link to="/app/support" className="track-link">support</Link>.
          </span>
        ) : (
          <span>
            This scanner needs a device license and no seat is free. Buy one or free an existing seat on the{' '}
            <Link to="/app/devices" className="track-link">Devices</Link> page.
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="seat-offer">
      <div className="seat-offer-q">
        {seat.reattach
          ? 'This scanner had a license seat that was freed. Put it back on this scanner?'
          : `You have ${seat.spare} unused license seat${seat.spare === 1 ? '' : 's'}. Allocate one to this scanner?`}
      </div>
      {/* The cooldown is the only lasting consequence, so it is stated before the click, not after. */}
      <div className="seat-offer-note">
        {seat.reattach
          ? 'Re-attaching a seat to the scanner it came from is always allowed, with no waiting.'
          : 'The seat stays with this scanner. Freeing it later starts a 30-day wait before it can move to a different scanner — Replace, on the Devices page, moves it immediately if this one fails.'}
      </div>
      {err && <p className="cart-error" style={{ marginTop: 8 }}>{err}</p>}
      <div className="seat-offer-actions">
        <button
          className="btn btn-primary btn-sm"
          disabled={allocate.isPending}
          onClick={() => {
            setErr(null);
            allocate.mutate({ licenseId: seat.licenseId, deviceId: seat.deviceId });
          }}
        >
          {allocate.isPending ? 'Allocating…' : seat.reattach ? 'Re-attach license' : 'Allocate license'}
        </button>
        <Link to="/app/devices" className="btn btn-ghost btn-sm">Manage on Devices</Link>
      </div>
    </div>
  );
}
